/**
 * Pictures in a conversation (D-751). Mirrors `AttachmentEndpoints.cs`; the
 * server owns every rule, this only asks.
 *
 * **Three steps, and the middle one usually skips the app.** She asks where to
 * write, the browser puts the bytes there, and the picture becomes real when a
 * message carries it. On a host with no upload address — local disk, so a
 * developer's machine and the whole test suite (D-669) — the bytes go through
 * the app instead, and the server decides which by whether it hands back a URL.
 *
 * **Reading one is an ordinary request to the app**, never a storage address:
 * a signed URL outlives the seat it was issued against and cannot be withdrawn,
 * and this is a photograph of somebody's child.
 */

import { fitWithin, renameToJpeg } from './imageResize'
import { landed, transfer } from './mediaUpload'
import type { Measured } from './mediaUpload'

/** One picture on a message, as the server describes it. */
export type MessageAttachment = {
  id: string
  fileName: string
  contentType: string
  sizeBytes: number
  /** The uploader's claim, for holding the bubble's place while it decodes. Never authority. */
  width: number | null
  height: number | null
}

/** Mirrors `MessageEndpoints.MaxAttachments`, and a test reads it out of that file. */
export const MAX_PICTURES_PER_MESSAGE = 6

/** Mirrors `AttachmentEndpoints.AllowedTypes`: only what every browser in the studio can draw. */
export const SENDABLE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  // Mirrored from the practice-video door, never invented here.
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-m4v',
]

/**
 * What the file picker offers.
 *
 * **Wider than what may be sent**, on purpose: an iPhone's own photographs are
 * HEIC, and a picker that refused them would look broken to the one person most
 * likely to be sending a photograph. They are converted below.
 */
export const PICKER_ACCEPT = 'image/*,video/*,application/pdf'

/** Where the bytes come from. Built from ids, so nothing here is a storage address. */
export function attachmentUrl(conversationId: string, attachmentId: string): string {
  return `/api/conversations/${conversationId}/attachments/${attachmentId}`
}

/** The studio's door onto the same picture (D-626), audited and on the review switch. */
export function attachmentReviewUrl(conversationId: string, attachmentId: string): string {
  return `${attachmentUrl(conversationId, attachmentId)}/review`
}

/** Wide enough to fill a bubble on a large screen; nothing in a chat is printed. */
const MAX_EDGE = 1600

const QUALITY = 0.82

export type Sendable = {
  file: File
  width: number
  height: number
}

/**
 * Whatever she picked, turned into something every browser can draw.
 *
 * **Everything goes through the canvas, not only the big ones.** The site's
 * photograph upload leaves a small file alone (`shouldResize`), which is right
 * there and wrong here: an iPhone HEIC is often under half a megabyte, and
 * passing it through would put a picture in the conversation that Safari draws
 * and nobody else does — a failure the sender never sees and cannot diagnose.
 * Re-encoding is also where the dimensions come from, which is what stops the
 * transcript jumping as each picture decodes.
 *
 * **A GIF is the exception and is sent as it came**: a canvas keeps one frame,
 * so re-encoding an animation silently throws the animation away.
 *
 * Returns null when the browser cannot decode the file at all — a Windows
 * laptop handed a HEIC, most often. The caller says so in a sentence rather
 * than uploading something nobody can open.
 */
/** Types that must not go through the canvas. */
function isPassedThrough(contentType: string): boolean {
  const type = contentType.toLowerCase()
  return type === 'image/gif' || type === PDF || type.startsWith('video/')
}

/**
 * Why this file cannot be sent, or null if it can (D-895).
 *
 * **Images are admitted by their prefix, everything else by the list.** An
 * iPhone's own photographs are HEIC, which is not in {@link SENDABLE_TYPES} and
 * is converted on the way out — so an image is judged on being an image, and a
 * video or a document is judged on being one the server will take.
 *
 * ⚠ **It exists because the compose box dropped them silently.** The picker
 * offered video and PDF, the server accepted both, the bubble rendered both —
 * and one `startsWith('image/')` in between skipped everything else with a bare
 * `continue`. What a teacher saw was the picker closing and nothing appearing,
 * which reads as the app ignoring her rather than refusing her.
 */
export function refusalFor(file: File): string | null {
  if (file.type.startsWith('image/') || SENDABLE_TYPES.includes(file.type)) return null

  return file.type.startsWith('video/')
    ? `${file.name} is a kind of video a message cannot carry. MP4 and MOV both work.`
    : `${file.name} is not a kind of file a message can carry.`
}

/** The one thing she can attach that is not a picture. */
export const PDF = 'application/pdf'

export function isPdf(contentType: string | null | undefined): boolean {
  return (contentType ?? '').toLowerCase() === PDF
}

/** A video plays in place; it is neither a picture nor a file row. */
export function isVideo(contentType: string | null | undefined): boolean {
  return (contentType ?? '').toLowerCase().startsWith('video/')
}

export async function toSendable(file: File): Promise<Sendable | null> {
  /*
   * **A GIF and a PDF both go up untouched**, for the same reason by two
   * routes: re-encoding a GIF through a canvas keeps one frame and loses the
   * animation, and a PDF is not an image at all - drawing it would produce a
   * blank JPEG and call it success.
   */
  if (isPassedThrough(file.type)) {
    return { file, width: 0, height: 0 }
  }

  try {
    const bitmap = await createImageBitmap(file)
    const { width, height } = fitWithin(bitmap.width, bitmap.height, MAX_EDGE)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) {
      bitmap.close()
      return null
    }

    context.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    )

    if (!blob) return null

    /*
     * Keep whichever is smaller, but only where the original was already a kind
     * the server accepts — a HEIC that re-encodes *larger* still has to be the
     * JPEG, or it arrives as something most of the group cannot see.
     */
    const keepOriginal =
      blob.size >= file.size && SENDABLE_TYPES.includes(file.type.toLowerCase())

    return keepOriginal
      ? { file, width, height }
      : { file: new File([blob], renameToJpeg(file.name), { type: 'image/jpeg' }), width, height }
  } catch {
    return null
  }
}

type Reserved = { attachmentId: string; uploadUrl: string | null; expiresAt: string }

/**
 * Puts one picture where the server says, and returns what a message needs to
 * carry it.
 *
 * Throws with a sentence a person can read; the caller shows it beside the
 * thumbnail rather than losing the whole message.
 */
/**
 * Runs one step of an upload and says which one failed (D-899).
 *
 * ⚠ **A rejected `fetch` is a bare `TypeError: Failed to fetch`**, and that is
 * what a teacher was shown when a video would not upload from her phone. It
 * names no step, so it cannot tell a refused reservation from a transfer that
 * died halfway — and those have opposite remedies. `res.ok` checks cannot
 * catch it: the promise never resolved.
 *
 * The step's own name is added rather than replacing the browser's words,
 * because the browser's words are the only clue about *why* the connection
 * failed.
 */
async function during<T>(step: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (e) {
    const said = e instanceof Error ? e.message : String(e)

    // Our own refusals already read as sentences; only a network rejection
    // needs the step bolted on.
    throw e instanceof TypeError ? new Error(`${step}: ${said}`) : e
  }
}

/**
 * This file is never going, however many times she taps (D-913).
 *
 * ⚠ **The distinction exists because a retry button has to mean something.**
 * Most upload failures are worth another go — a dead connection, a locked
 * phone, a spent reservation — but a file this browser cannot decode will fail
 * identically for ever, and offering to try it again is the
 * control-that-does-nothing D-895 already had reported once.
 *
 * *Found by a pre-existing test, not by reading: the first version of the retry
 * keyed on "did a transfer start", which is true of this case too, so a
 * Windows laptop handed a HEIC was offered a button that could not work.*
 */
export class CannotSendThisFile extends Error {}

/**
 * Everything optional about putting one file in a conversation.
 *
 * **An object rather than four trailing parameters**, which is what this was
 * until 2026-10-19 — and at five it was already a call site nobody could read
 * without counting commas. The shared core took the same correction for the
 * same reason (D-909).
 */
export type AttachmentUpload = {
  onProgress?: (fraction: number) => void
  /**
   * Stops the transfer when she takes the picture back off the message
   * (D-908).
   *
   * ⚠ **This door took no signal at all until 2026-10-19**, so removing a tile
   * revoked its thumbnail and dropped the row **while the upload carried on**.
   * On a photograph that was waste nobody saw; on a 254 MB video over cellular
   * it is minutes of her data spent on something she has already taken back —
   * and with blocks it reads as a cancel button that does not cancel.
   */
  signal?: AbortSignal
  /**
   * How fast her connection really is, and what had gone when it was read
   * (D-912).
   *
   * **The only honest source of it.** `navigator.connection` does not exist
   * on iOS Safari at all, and where it does it estimates downlink and buckets
   * everything above 4G together - so it can tell neither 5G from a weak bar
   * nor anything at all about sending. Real bytes to the real destination
   * answer it on every phone. The screen uses it to say how long this will
   * take, and to decide whether a smaller version is worth offering.
   *
   * **Which window it is measured over, and when it is withheld, belong to the
   * core** (`measureAfter` and `windowImpaired` in `mediaUpload.ts`). Do not
   * restate the rule here: this door carried a copy saying *"the first block"*
   * for exactly as long as that was wrong.
   */
  onRate?: (measured: Measured) => void
  /**
   * The real dimensions of a file that has already been through an encoder
   * (D-903, D-912), so the canvas path is skipped and the bubble gets a shape.
   *
   * **Measured off the output, never worked out from the input.** A video
   * normally arrives here with no dimensions at all — `toSendable` passes it
   * through and returns zeroes, which `aspectOf` turns into a square — and the
   * one moment the truth is known is the encode that produced the file. Not
   * passing it on would be throwing away a measurement already paid for.
   */
  dimensions?: { width: number; height: number }
}

export async function uploadPicture(
  conversationId: string,
  chosen: File,
  { onProgress, signal, onRate, dimensions }: AttachmentUpload = {},
): Promise<MessageAttachment> {
  /*
   * **A file that has already been encoded does not go through the canvas
   * again** (D-912). It is a video, so `toSendable` would only pass it through
   * and hand back zeroes — but it would also be the second lossy pass on
   * something that has had one, if the kind ever changed.
   *
   * ⚠ **The bypass is bounded by the type, not by trust in the caller.** Open
   * to anything, a future caller passing dimensions for a HEIC would skip the
   * conversion `toSendable` exists for and put a picture in the conversation
   * that only an Apple device can draw — and the sender, holding the one device
   * that can, would never see it.
   */
  const ready =
    dimensions && isPassedThrough(chosen.type)
      ? { file: chosen, ...dimensions }
      : await toSendable(chosen)

  if (!ready) {
    throw new CannotSendThisFile(
      `This browser cannot read ${chosen.name}. Try a JPEG or PNG - on an iPhone, Settings, Camera, Formats, Most Compatible.`,
    )
  }

  const { file, width, height } = ready

  const reserve = await during('Asking the studio where to put it', () =>
    fetch(`/api/conversations/${conversationId}/attachments`, {
      method: 'POST',
      // Taking a tile back off before the reservation has even answered must
      // stop here too, not only at the transfer below.
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        width: width || null,
        height: height || null,
      }),
    }))

  if (!reserve.ok) throw new Error(await refusalOf(reserve, 'That picture could not be added.'))

  const ticket: Reserved = await reserve.json()

  /*
   * <b>The write is the shared core's</b> (D-909) — the branch between storage
   * and the app, the headers, the progress and the abort signal are the same
   * two doors' worth of code, and they had already drifted. What stays here is
   * the wording and what a status *means*, both of which are this door's.
   */
  /*
   * <b>The size is in the label, because it is the first thing anybody will
   * ask</b> (D-900). A transfer that dies on a phone and one a storage limit
   * refuses look identical from here, and the number separates them faster
   * than another round of guessing: if it fails above some threshold and never
   * below, that is a limit rather than a bad connection.
   *
   * <b>Built once</b> — a dead transfer and a refusal must name the same step,
   * which is the whole distinction D-899 exists to draw, and two copies of the
   * arithmetic would eventually disagree about it.
   */
  const step = `Sending it to storage (${Math.round(file.size / (1024 * 1024))} MB)`

  const written = await transfer(
    file,
    {
      uploadUrl: ticket.uploadUrl,
      throughApp: `/api/conversations/${conversationId}/attachments/${ticket.attachmentId}/content`,
    },
    { sending: () => step },
    { onProgress, signal, onRate },
  )

  /*
   * <b>A refused commit is not a failure she needs to see</b> (D-906). A
   * create-only destination refuses a second commit exactly as it refuses an
   * expired one, and the likeliest cause is a transfer that landed while the
   * phone changed network and lost the answer. **Sending the message is what
   * settles it**: the server asks storage what is actually there and refuses an
   * attachment that never arrived, so a wrong guess here costs her a message
   * she has to send twice, while a wrong refusal costs her the video.
   */
  if (!landed(written) && !written.settleWithTheFinishingStep) {
    /*
     * <b>Storage names its status; the app is asked for its own sentence.</b>
     * A signed URL answers with no body worth reading, and the remedy for its
     * refusals is the same one — a block refused under a create-only
     * permission is a spent or expired reservation, and a fresh one is the way
     * out. The app, by contrast, sends a sentence written for a person.
     */
    throw written.via === 'storage'
      ? new Error(`${step}: storage answered ${written.status}. Remove it and add it again.`)
      : new Error(sentenceFrom(written.text, 'That picture did not finish uploading.'))
  }

  return {
    id: ticket.attachmentId,
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
    width: width || null,
    height: height || null,
  }
}

/** The server's own sentence where it sent one, and ours where it did not. */
async function refusalOf(res: Response, fallback: string): Promise<string> {
  try {
    return sentenceFrom(JSON.stringify(await res.json()), fallback)
  } catch {
    return fallback
  }
}

/**
 * The server's own sentence out of a problem-details body, or ours.
 *
 * The text form rather than the `Response` form, because the shared core hands
 * back what the server said rather than the response object — it has already
 * been read by the time this door gets to judge it.
 */
function sentenceFrom(body: string, fallback: string): string {
  try {
    const problem = JSON.parse(body)
    const detail = typeof problem?.detail === 'string' ? problem.detail : null
    const title = typeof problem?.title === 'string' ? problem.title : null

    return detail ?? title ?? fallback
  } catch {
    return fallback
  }
}

/**
 * The shape a bubble reserves before the picture decodes.
 *
 * Without it every arriving photograph shoves the conversation down as it
 * loads, which on a live stream happens while somebody is reading (D-750).
 * Falls back to a square, which is wrong but stable — the jump is the problem,
 * not the aspect ratio.
 */
export function aspectOf(picture: { width: number | null; height: number | null }): string {
  const { width, height } = picture

  if (!width || !height || width <= 0 || height <= 0) return '1 / 1'

  return `${width} / ${height}`
}

/**
 * "284 KB", "1.2 MB" - the same shape the media library uses.
 *
 * **Bytes below a kilobyte**, because rounding those to `0 KB` reads as
 * something having gone wrong. Nothing a phone takes is that small; the deep
 * sweep's own one-pixel picture is, and it is what made the wrong answer
 * visible.
 */
export function describeSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`

  return `${Math.max(0, Math.round(bytes))} bytes`
}
